/**
 * The site map's own grouping.
 *
 * Deliberately NOT the sidebar's grouping.  The sidebar is organised around how the product was
 * built — Administration holds reports, users, providers and system settings because that is
 * where those screens were added.  This groups by what someone is trying to *do*, so a person
 * looking for "where do I check a claim" finds every billing screen in one place regardless of
 * which sidebar section it happens to live under.
 *
 * Order here is fixed and identical for every viewer.  Roles change what is present, never where
 * it sits, so two people can talk about "the third item under Billing" and mean the same screen.
 *
 * Access is not defined here.  Each entry names the menu-catalog keys that grant it, and the page
 * asks the catalog whether a role can reach any of them — so the site map cannot drift out of
 * step with the sidebar or show a link that would bounce the user.
 *
 * It is also AI Search's list of pages it can point people to: CI publishes it for the agent when
 * it changes (scripts/export-ai-search-pages.ts), so a page added here is one it can name.  Keep
 * this file free of imports, so that script can load it with plain ts-node.
 */

export type SitemapEntry = {
  /** Menu-catalog item keys that reach this page.  Any one of them visible means it shows. */
  keys: string[];
  /**
   * Only for ungated entries, which have no menu-catalog item to take a name from.  Every other
   * entry shows the sidebar's own label, so renaming there renames here in the same edit.
   */
  label?: string;
  /** Fallback destination.  Gated entries take their path from the catalog instead. */
  path: string;
  /** What the page is for.  Shown on hover, since the list itself stays dense. */
  blurb: string;
  /** Draws a rule under this entry, separating it from what follows in the same group. */
  breakAfter?: boolean;
  /** Statuses the page has a tab for, opened with `?status=` (shared/lib/hooks/useStatusTabFromUrl). */
  tabs?: string[];
};

/** Entries reachable by anyone, regardless of role — they have no menu-catalog gate. */
export const UNGATED = '*';

export type SitemapGroup = {
  title: string;
  blurb: string;
  entries: SitemapEntry[];
};

const START_HERE: SitemapGroup = {
  title: 'Start here',
  blurb: 'Where everyone lands, and what to check while you are there.',
  entries: [
    {
      keys: [UNGATED],
      label: 'Home',
      path: '/care-management/patients',
      blurb: 'Your patient list — the same place the logo goes.',
      breakAfter: true
    },
    {
      keys: ['admin.announcements', 'quick.announcements'],
      path: '/administration/announcements',
      blurb: 'Notices shown across the portal.'
    },
    { keys: [UNGATED], label: 'Notifications', path: '/notifications', blurb: 'Everything the portal has flagged for you.' },
    { keys: ['admin.resources'], path: '/administration/resources', blurb: 'Shared documents and reference material.' }
  ]
};

const FINDING_PATIENTS: SitemapGroup = {
  title: 'Finding patients',
  blurb: 'Every way into a patient record.',
  entries: [
    {
      keys: ['patients.lookup'],
      path: '/patients/lookup',
      blurb: 'Search by name, phone, DOB, Truentity or EHR ID.'
    },
    {
      keys: ['patients.tagged'],
      path: '/patients/tagged',
      blurb: 'Patients grouped by the tags your team applies.'
    },
    { keys: ['patients.mine'], path: '/patients', blurb: 'The patients assigned to you.' },
    {
      keys: ['patients.followUp'],
      path: '/patients/patients-to-followup',
      blurb: 'Outstanding follow-ups waiting on a call.'
    },
    {
      keys: ['patients.conversations'],
      path: '/patients/conversations',
      blurb: 'Messages exchanged with patients.'
    },
    {
      keys: ['patients.medHistory'],
      path: '/patients/med-history-requests',
      blurb: 'Medication history requests and their results.'
    },
    {
      keys: ['admin.patients'],
      path: '/administration/patients',
      blurb: 'Cross-pharmacy patient records and merges.'
    }
  ]
};

const CARE_MANAGEMENT: SitemapGroup = {
  title: 'Care management',
  blurb: 'Day-to-day monitoring, alerts and the work queues built from them.',
  entries: [
    {
      keys: ['rpm.patients'],
      path: '/care-management/patients',
      blurb: 'Enrolled patients and their monitoring status.',
      tabs: ['IS_CANDIDATE', 'SCHEDULED_FOR_ENROLLMENT', 'ENROLLED', 'UNENROLLED']
    },
    {
      keys: ['rpm.dashboard'],
      path: '/care-management/dashboard',
      blurb: 'Programme health at a glance.'
    },
    { keys: ['rpm.alerts'], path: '/remote-patient-monitoring/alerts', blurb: 'Readings that crossed a threshold.' },
    {
      keys: ['rpm.followUpTasks'],
      path: '/remote-patient-monitoring/follow-up-tasks',
      blurb: 'Tasks raised from alerts and encounters.'
    },
    {
      keys: ['rpm.devices'],
      path: '/remote-patient-monitoring/devices',
      blurb: 'Monitoring devices, assignment and connectivity.'
    },
    {
      keys: ['rpm.reportsCommunication'],
      path: '/remote-patient-monitoring/reports-communication',
      blurb: 'Reports sent out to providers and partners.'
    }
  ]
};

const PROVIDER: SitemapGroup = {
  title: 'Provider',
  blurb: 'The queues a clinician works through, and the sign-offs that close them.',
  entries: [
    {
      keys: ['rpm.patientSchedules'],
      path: '/remote-patient-monitoring/patient-schedules',
      blurb: 'Scheduled patient touchpoints.'
    },
    {
      keys: ['rpm.workqueue'],
      path: '/care-management/workqueue',
      blurb: 'The shared queue of care work to pick up.'
    },
    {
      keys: ['rpm.chartReview'],
      path: '/remote-patient-monitoring/review-patients',
      blurb: 'Charts waiting on a clinician review.'
    },
    {
      keys: ['rpm.providerApprovals'],
      path: '/remote-patient-monitoring/provider-approvals',
      blurb: 'Provider sign-off requests awaiting a decision.'
    },
    {
      keys: ['reports.rpmProviderSignoffs'],
      path: '/reports/rpm-provider-signoffs',
      blurb: 'Outstanding and completed provider sign-offs.'
    }
  ]
};

const MEDICATION_MANAGEMENT: SitemapGroup = {
  title: 'Medication management',
  blurb: 'MTM work and the queue it generates.',
  entries: [
    { keys: ['mtm.tasks'], path: '/tasks', blurb: 'Medication review work assigned to you or your team.' },
    {
      keys: ['mtm.myDashboard'],
      path: '/reports/my-dashboard',
      blurb: 'Your own throughput and outstanding work.'
    }
  ]
};

const TRANSITIONS_OF_CARE: SitemapGroup = {
  title: 'Transitions of care',
  blurb: 'Admissions, discharges and the TCM follow-up window.',
  entries: [
    {
      keys: ['tcm.dashboard'],
      path: '/transitional-care-management/dashboard',
      blurb: 'Transitional care programme overview.'
    },
    {
      keys: ['tcm.adtQueue'],
      path: '/transitional-care-management/adt-queue-v1',
      blurb: 'Incoming admission and discharge events.'
    },
    {
      keys: ['tcm.tasks'],
      path: '/transitional-care-management/tcm-tasks',
      blurb: 'Follow-up work inside the TCM window.'
    },
    {
      keys: ['tcm.discharge'],
      path: '/transitional-care-management/discharge',
      blurb: 'Patients discharged and awaiting outreach.'
    }
  ]
};

const REPORTING: SitemapGroup = {
  title: 'Reporting and analytics',
  blurb: 'Read-only views of how the programme and the people in it are performing.',
  entries: [
    {
      keys: ['reports.dashboard'],
      path: '/reports/dashboard',
      blurb: 'The top-level reporting landing page.'
    },
    { keys: ['reports.performance'], path: '/reports/performance', blurb: 'Team performance against targets.' },
    {
      keys: ['admin.myPerformance', 'quick.myPerformance'],
      path: '/administration/my-performance',
      blurb: 'Your own numbers.'
    },
    { keys: ['reports.userTime'], path: '/reports/session-time', blurb: 'Time users spent in the portal.' },
    { keys: ['reports.encounters'], path: '/reports/encounters', blurb: 'Encounters logged across the pharmacy.' },
    { keys: ['reports.tasks'], path: '/reports/tasks', blurb: 'Task volume and completion.' },
    { keys: ['reports.healthPlan'], path: '/reports/health-plan', blurb: 'Activity broken down by health plan.' },
    { keys: ['reports.rpmMetrics'], path: '/reports/rpm-metrics', blurb: 'Monitoring programme metrics.' },
    { keys: ['reports.ccmReports'], path: '/reports/ccm-reports', blurb: 'Chronic care management reporting.' },
    {
      keys: ['reports.rpmProviders'],
      path: '/reports/rpm-providers',
      blurb: 'Provider participation in monitoring.'
    },
    {
      keys: ['reports.rpmCompanies'],
      path: '/reports/rpm-companies',
      blurb: 'Monitoring activity across pharmacies.'
    },
    {
      keys: ['reports.rpmChartReviews'],
      path: '/reports/rpm-chart-reviews',
      blurb: 'Chart review throughput.'
    },
    {
      keys: ['reports.payerChartRequests'],
      path: '/reports/payer-chart-requests',
      blurb: 'Chart requests raised by payers.'
    },
    {
      keys: ['admin.rxEventMetrics'],
      path: '/administration/rx-event-metrics',
      blurb: 'Prescription event volumes.'
    }
  ]
};

const PEOPLE_AND_ACCESS: SitemapGroup = {
  title: 'People and access',
  blurb: 'Who works here, who they are, and when they are available.',
  entries: [
    { keys: ['users.status'], path: '/administration/dashboard', blurb: 'Who is active and what they are doing.' },
    { keys: ['users.manage'], path: '/administration/users', blurb: 'Create users and set their roles.' },
    {
      keys: ['providers.directory', 'quick.providers'],
      path: '/administration/providers/directory',
      blurb: 'Providers your pharmacy works with.'
    },
    {
      keys: ['providers.availability'],
      path: '/administration/providers/availability',
      blurb: 'When providers can be booked.'
    }
  ]
};

const COMPANY_AND_SYSTEM: SitemapGroup = {
  title: 'Pharmacy and system',
  blurb: 'Configuration, reference data and the audit trail.',
  entries: [
    {
      keys: ['admin.organizations'],
      path: '/administration/organizations',
      blurb: 'Client organisations and their stores.'
    },
    {
      keys: ['admin.healthPlans'],
      path: '/administration/health-plans',
      blurb: 'Health plans available to patients.'
    },
    {
      keys: ['admin.insurancePlans'],
      path: '/administration/insurance-plans',
      blurb: 'Insurance plan records and merges.'
    },
    { keys: ['system.myCompany'], path: '/administration/system/companies', blurb: 'Your own pharmacy’s settings.' },
    {
      keys: ['system.allCompanies'],
      path: '/administration/system/companies',
      blurb: 'Every pharmacy on the platform.'
    },
    {
      keys: ['system.configurations'],
      path: '/administration/system/configurations',
      blurb: 'Feature and behaviour settings.'
    },
    {
      keys: ['system.dataImport'],
      path: '/administration/system/data-import',
      blurb: 'Bulk imports and their results.'
    },
    {
      keys: ['system.eligibilityChecks'],
      path: '/administration/system/eligibility-checks',
      blurb: 'Bulk insurance eligibility checks: history and new runs.'
    },
    {
      keys: ['system.notificationFiles'],
      path: '/administration/system/notification-files',
      blurb: 'Generated notification files.'
    },
    {
      keys: ['admin.logs', 'quick.logs'],
      path: '/administration/logs',
      blurb: 'Audit trail of what happened and who did it.'
    },
    {
      keys: ['admin.athenaApis'],
      path: '/administration/mock-apis',
      blurb: 'Mock Athena endpoints, outside production.'
    },
    { keys: ['reports.billing'], path: '/reports/billing', blurb: 'Billable activity across the pharmacy.' },
    { keys: ['reports.claims'], path: '/reports/claims', blurb: 'Claim status and rejections.' },
    {
      keys: ['rpm.claims'],
      path: '/remote-patient-monitoring/claims',
      blurb: 'Claims generated from monitoring activity.'
    },
    {
      keys: ['tcm.billing'],
      path: '/transitional-care-management/billing',
      blurb: 'Billing for transitional care episodes.'
    },
    {
      keys: ['timesheets.timesheets'],
      path: '/administration/timesheets',
      blurb: 'Recorded time, yours or your team’s.'
    },
    {
      keys: ['timesheets.invoices'],
      path: '/administration/invoices',
      blurb: 'Invoices raised against recorded time.'
    }
  ]
};

/**
 * How the page is laid out: sections, each of three columns, each column stacking its groups.
 *
 * Reading order is section by section, column by column — that is also the order the access
 * tests treat as canonical, and it never changes between roles.
 *
 * Section one is who you are looking after, section two the work in front of you, section three
 * the running of the business.
 *
 * One group per column, deliberately.  Stacking four groups into a third column left it running
 * far below the two beside it — worst for a provider, whose other columns hold two or three links
 * while that one held twelve.
 */
export const SITEMAP_LAYOUT: SitemapGroup[][][] = [
  [[START_HERE], [FINDING_PATIENTS], [CARE_MANAGEMENT]],
  [[PROVIDER], [MEDICATION_MANAGEMENT], [TRANSITIONS_OF_CARE]],
  [[REPORTING], [COMPANY_AND_SYSTEM], [PEOPLE_AND_ACCESS]]
];

/** The same groups as a flat list, in reading order, for anything that does not lay them out. */
export const SITEMAP: SitemapGroup[] = SITEMAP_LAYOUT.flat(2);
